import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

import TiltedCard from "../../animations/TiltedCard";
import SpotlightCard from "../../animations/SpotlightCard";
import GlareHover from "../../animations/GlareHover";

const TimelineItem = ({ item, index }) => {
  const Icon = item.icon;

  return (
    <motion.div
      initial={{
        opacity: 0,
        x: index % 2 === 0 ? -60 : 60,
      }}
      whileInView={{
        opacity: 1,
        x: 0,
      }}
      viewport={{
        once: true,
        amount: 0.2,
      }}
      transition={{
        duration: 0.65,
        delay: index * 0.08,
      }}
      className="
        relative
        flex
        gap-4
        sm:gap-6
        md:gap-8
      "
    >
      {/* =====================================================
          TIMELINE
      ====================================================== */}
      <div className="relative flex flex-col items-center">
        {/* Animated Icon */}
        <motion.div
          whileHover={{
            rotate: 360,
            scale: 1.08,
          }}
          transition={{
            duration: 0.6,
          }}
          className="
            relative
            z-10
            flex
            h-10
            w-10
            flex-shrink-0
            items-center
            justify-center
            rounded-full
            border-4
            theme-border
            brand-bg
            shadow-[0_0_20px_rgba(249,115,22,.35)]
            sm:h-12
            sm:w-12
            md:h-14
            md:w-14
            lg:h-16
            lg:w-16
          "
        >
          <motion.div
            animate={{
              scale: [1, 1.08, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <Icon
              className="
                h-5
                w-5
                text-white
                sm:h-6
                sm:w-6
                md:h-7
                md:w-7
              "
            />
          </motion.div>
        </motion.div>

        {/* Timeline Line */}
        {index !== 7 && (
          <motion.div
            initial={{
              scaleY: 0,
            }}
            whileInView={{
              scaleY: 1,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.9,
              delay: 0.2,
            }}
            className="
              mt-2
              min-h-[calc(100%+40px)]
              w-[2px]
              flex-1
              origin-top
              rounded-full
              bg-gradient-to-b
              from-orange-500
              via-orange-400/70
              to-transparent
              sm:w-[3px]
            "
          />
        )}
      </div>

      {/* =====================================================
          CARD AREA
          mb creates visible space between cards
      ====================================================== */}
      <div
        className="
          timeline-tilted-wrapper
          min-w-0
          flex-1
          pb-10
          sm:pb-12
          lg:pb-14
        "
      >
        <TiltedCard
          containerHeight="100%"
          containerWidth="100%"
          className="timeline-tilted-card"
        >
          <SpotlightCard
            tailwindColor="var(--brand-primary)"
            alpha={0.12}
            radius={320}
            className="
              timeline-spotlight
              h-full
              w-full
              rounded-2xl
            "
          >
            <GlareHover
              borderRadius="20px"
              borderColor="rgba(249,115,22,.16)"
              glareColor="#E67000"
              glareOpacity={0.10}
              className="timeline-glare h-full w-full"
            >
              <motion.div
                whileHover={{
                  y: -4,
                }}
                transition={{
                  duration: 0.3,
                  ease: "easeOut",
                }}
                className="
                  timeline-content-card
                  group
                  relative
                  flex
                  min-h-full
                  flex-1
                  overflow-hidden
                  rounded-2xl
                  theme-card
                  theme-border
                  p-5
                  transition-all
                  duration-300
                  hover:border-orange-500/30
                  sm:p-6
                  lg:rounded-3xl
                  lg:p-8
                "
              >
                {/* =================================================
                    SUBTLE HOVER GLOW
                ================================================== */}
                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-0
                    bg-[radial-gradient(circle_at_top_right,#f9731618,transparent_70%)]
                    opacity-0
                    transition-opacity
                    duration-500
                    group-hover:opacity-100
                  "
                />

                {/* =================================================
                    TOP ACCENT
                ================================================== */}
                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-x-0
                    top-0
                    h-px
                    bg-gradient-to-r
                    from-transparent
                    via-orange-500/50
                    to-transparent
                    opacity-0
                    transition-opacity
                    duration-500
                    group-hover:opacity-100
                  "
                />

                <div className="relative z-10 w-full">
                  {/* =================================================
                      SECTION BADGE
                  ================================================== */}
                  <motion.span
                    initial={{
                      opacity: 0,
                      y: 12,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      delay: 0.1,
                    }}
                    className="
                      inline-flex
                      rounded-full
                      border
                      border-orange-500/30
                      bg-orange-500/10
                      px-3
                      py-1
                      text-[10px]
                      font-bold
                      uppercase
                      tracking-[0.2em]
                      brand-color
                      sm:px-4
                      sm:text-xs
                    "
                  >
                    Section {item.number}
                  </motion.span>

                  {/* =================================================
                      TITLE
                  ================================================== */}
                  <motion.h3
                    initial={{
                      opacity: 0,
                      y: 18,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      delay: 0.15,
                    }}
                    className="
                      mt-4
                      text-2xl
                      font-bold
                      leading-tight
                      theme-text-primary
                      transition-colors
                      duration-300
                      group-hover:text-orange-500
                      sm:text-3xl
                    "
                  >
                    {item.title}
                  </motion.h3>

                  {/* =================================================
                      DESCRIPTION
                  ================================================== */}
                  <motion.p
                    initial={{
                      opacity: 0,
                      y: 20,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      delay: 0.22,
                    }}
                    className="
                      mt-4
                      max-w-[1100px]
                      text-sm
                      leading-7
                      theme-text-muted
                      sm:text-base
                      sm:leading-8
                    "
                  >
                    {item.description}
                  </motion.p>

                  {/* =================================================
                      POINTS
                  ================================================== */}
                  <div className="mt-6 space-y-3 sm:mt-7 sm:space-y-4">
                    {item.points.map((point, i) => (
                      <motion.div
                        key={i}
                        initial={{
                          opacity: 0,
                          x: -18,
                        }}
                        whileInView={{
                          opacity: 1,
                          x: 0,
                        }}
                        viewport={{
                          once: true,
                        }}
                        transition={{
                          delay: i * 0.08,
                        }}
                        whileHover={{
                          x: 4,
                        }}
                        className="
                          group/item
                          flex
                          items-start
                          gap-3
                        "
                      >
                        {/* Check Icon */}
                        <div
                          className="
                            mt-1
                            flex
                            h-5
                            w-5
                            flex-shrink-0
                            items-center
                            justify-center
                            rounded-full
                            bg-orange-500/10
                            transition-all
                            duration-300
                            group-hover/item:bg-orange-500/15
                          "
                        >
                          <CheckCircle2
                            size={15}
                            className="
                              text-orange-500
                              transition-transform
                              duration-300
                              group-hover/item:scale-110
                            "
                          />
                        </div>

                        {/* Point Text */}
                        <p
                          className="
                            text-sm
                            leading-7
                            theme-text-secondary
                            transition-colors
                            duration-300
                            group-hover/item:text-[var(--text-primary)]
                            sm:text-base
                          "
                        >
                          {point}
                        </p>
                      </motion.div>
                    ))}
                  </div>

                  {/* =================================================
                      BOTTOM ACCENT
                  ================================================== */}
                  <motion.div
                    initial={{
                      scaleX: 0,
                    }}
                    whileInView={{
                      scaleX: 1,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      duration: 0.7,
                      delay: 0.3,
                    }}
                    className="
                      mt-7
                      h-[2px]
                      origin-left
                      rounded-full
                      bg-gradient-to-r
                      from-orange-500
                      via-orange-400/50
                      to-transparent
                    "
                  />
                </div>
              </motion.div>
            </GlareHover>
          </SpotlightCard>
        </TiltedCard>
      </div>
    </motion.div>
  );
};

export default TimelineItem;